import { gql, useMutation } from '@apollo/client'
import { yupResolver } from '@hookform/resolvers/yup'
import AddIcon from '@mui/icons-material/Add'
import CancelIcon from '@mui/icons-material/Cancel'
import {
  Box,
  Button,
  Divider,
  Fab,
  FormHelperText,
  Grid2 as Grid,
  InputLabel,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import Modal from 'components/Modal'
import { useGetMe } from 'components/Session/hooks'
import { VariantType, useSnackbar } from 'notistack'
import React, { useContext, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs from 'dayjs'
import * as Yup from 'yup'

import { DATAROOM_FRAGMENT } from '../../constants'
import { STAGE_FRAGMENT } from '../constants'
import DataroomContext, { DataroomContextType } from '../context'

const stageSchema = Yup.object({
  name: Yup.string().required('Please enter a name'),
  timestamp: Yup.date()
    .typeError('Invalid date format')
    .nullable()
    .default(null),
  notes: Yup.string().default(''),
})

export type StageDataType = Yup.InferType<typeof stageSchema>

const CREATE_STAGE = gql`
  ${DATAROOM_FRAGMENT}
  ${STAGE_FRAGMENT}
  mutation CREATE_STAGE($dataroomId: ID!, $stage: StageInput!) {
    createStage(dataroomId: $dataroomId, stage: $stage) {
      ...DataroomFragment
      stages {
        ...StageFragment
      }
    }
  }
`

type StageFormProps = {
  values?: StageDataType
  onStage: (data: StageDataType) => void
}

export function StageForm({ onStage, values }: StageFormProps) {
  const { control, handleSubmit } = useForm({
    defaultValues: values || {
      name: '',
      timestamp: null as Date | null,
      notes: '',
    },
    resolver: yupResolver(stageSchema),
  })

  return (
    <Box component="form" onSubmit={handleSubmit(onStage)}>
      <Grid container spacing={2}>
        <Grid size={6}>
          <Controller
            name="name"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <React.Fragment>
                <InputLabel htmlFor="name">
                  <Typography sx={theme => ({ ...theme.typography.caption })}>
                    Name*
                  </Typography>
                </InputLabel>
                <TextField
                  id="name"
                  error={!!error}
                  onChange={onChange}
                  value={value}
                  fullWidth
                  variant="outlined"
                  size="small"
                  helperText={error && error.message}
                />
              </React.Fragment>
            )}
          />
        </Grid>
        <Grid size={6}>
          <Controller
            name="timestamp"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <React.Fragment>
                <InputLabel htmlFor="timestamp">
                  <Typography sx={theme => ({ ...theme.typography.caption })}>
                    Timestamp
                  </Typography>
                </InputLabel>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={value ? dayjs(new Date(value)) : null}
                    onChange={date => onChange(date?.toDate())}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        error: !!error,
                      },
                      field: { clearable: true },
                    }}
                  />
                </LocalizationProvider>
                {error && (
                  <FormHelperText error>{error.message}</FormHelperText>
                )}
              </React.Fragment>
            )}
          />
        </Grid>
        <Grid size={12}>
          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <React.Fragment>
                <InputLabel htmlFor="notes">
                  <Typography sx={theme => ({ ...theme.typography.caption })}>
                    Notes
                  </Typography>
                </InputLabel>
                <TextField
                  {...field}
                  placeholder="Your notes..."
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  variant="outlined"
                />
              </React.Fragment>
            )}
          />
        </Grid>
      </Grid>
      <Divider sx={{ mt: 1, mb: 1 }} />
      <Button
        type="submit"
        sx={{ float: 'right' }}
        variant="contained"
        disableElevation
      >
        Save
      </Button>
    </Box>
  )
}

function Create() {
  const { dataroom } = useContext<DataroomContextType>(DataroomContext)
  const { data: meData } = useGetMe()

  const [open, setOpen] = useState(false)
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()

  const handleNotification = (variant: VariantType, message: string) => () => {
    enqueueSnackbar(message, { variant, autoHideDuration: 3000 })
  }

  const onSuccess = handleNotification(
    'success',
    'New Stage created successfully!'
  )
  const onError = handleNotification('error', 'Something went wrong!')

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const [createStage] = useMutation(CREATE_STAGE)

  const handleStage = (data: StageDataType) => {
    createStage({
      variables: {
        dataroomId: dataroom.id,
        stage: {
          user: meData?.me?.id,
          company: meData?.me?.company?.id,
          ...data,
        },
      },
      onCompleted: () => {
        handleClose()
        onSuccess()
      },
      onError,
    })
  }

  return (
    <React.Fragment>
      <Modal open={open} width="75%" maxWidth={720}>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography
            sx={{ ...theme.typography.h5, color: theme.palette.text.primary }}
          >
            Create Stage
          </Typography>
          <CancelIcon
            onClick={handleClose}
            sx={{ cursor: 'pointer', color: theme.palette.error.main }}
          />
        </Stack>
        <Divider sx={{ mt: 1, mb: 1 }} />
        <StageForm onStage={handleStage} />
      </Modal>
      <Fab
        color="primary"
        aria-label="add"
        onClick={handleOpen}
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
      >
        <AddIcon />
      </Fab>
    </React.Fragment>
  )
}

export default Create
